import { useCallback, useRef, useState } from 'react';
import { AdresseSaisie } from '../../utils/adresse';

export type StatutSirene = 'trouve' | 'inconnu' | 'indisponible';

export interface ResultatVerificationSiret {
  /** SIRET deja porte par un organisme ou une entreprise Centralis. */
  existe: boolean;
  /** Pourquoi l'adresse SIRENE est la ou manque ; null quand le SIRET existe deja (pas de recherche). */
  statutSirene: StatutSirene | null;
  adresse: AdresseSaisie | null;
}

const INDISPONIBLE: ResultatVerificationSiret = { existe: false, statutSirene: 'indisponible', adresse: null };

const adresseDepuisSirene = (brut: Record<string, string | null> | null): AdresseSaisie | null => {
  if (!brut) return null;
  return {
    numero_voie: brut.numero_voie ?? '',
    type_voie: brut.type_voie ?? '',
    nom_voie: brut.nom_voie ?? '',
    code_postal: brut.code_postal ?? '',
    ville: brut.ville ?? '',
  };
};

/**
 * Controle d'un SIRET a l'inscription : unicite sur Centralis et adresse SIRENE.
 *
 * Ne throw jamais : un reseau en panne vaut « repertoire indisponible » et laisse l'utilisateur
 * saisir son adresse. Une reponse arrivee apres un nouvel appel de `verifier` ou d'`annuler`
 * est ignoree (retour null) pour ne pas pre-remplir le formulaire avec l'adresse d'un numero
 * abandonne : l'appelant doit donc appeler `annuler` des que le SIRET change ou que le
 * formulaire est reinitialise.
 */
export const useVerificationSiret = () => {
  const [chargement, setChargement] = useState(false);
  const derniereDemande = useRef(0);

  const annuler = useCallback(() => {
    derniereDemande.current += 1;
    setChargement(false);
  }, []);

  const verifier = useCallback(async (siret: string): Promise<ResultatVerificationSiret | null> => {
    const demande = ++derniereDemande.current;
    const estCourante = () => demande === derniereDemande.current;
    setChargement(true);
    try {
      const reponse = await fetch(`${process.env.REACT_APP_API_URL}/inscription/check-siret/${siret}`);
      const json = reponse.ok ? await reponse.json() : null;
      if (!estCourante()) return null;
      if (!json || json.status !== 'success') return INDISPONIBLE;
      return {
        existe: json.exists === true,
        statutSirene: json.sirene_statut ?? null,
        adresse: adresseDepuisSirene(json.adresse_sirene ?? null),
      };
    } catch {
      return estCourante() ? INDISPONIBLE : null;
    } finally {
      if (estCourante()) setChargement(false);
    }
  }, []);

  return { verifier, annuler, chargement };
};
